<template>
  <button
    type="button"
    class="ChipFilter"
    :class="{
      'is-selected': selected,
      'is-disabled': disabled,
    }"
    :aria-pressed="selected"
    :disabled
  >
    <Icon
      v-if="leftIcon"
      :icon="leftIcon"
      size="sm"
      class="ChipFilter-leftIcon"
    />

    <span v-if="$slots.default" class="ChipFilter-label">
      <slot />
    </span>

    <span
      v-if="selected && !readonly"
      role="button"
      tabindex="0"
      class="ChipFilter-closeIcon"
      @click.stop="$emit('clear')"
      @keydown.enter.stop.prevent="$emit('clear')"
      @keydown.space.stop.prevent="$emit('clear')"
    >
      <Icon icon="close-circle" size="sm" />
    </span>

    <Icon
      v-else-if="rightIcon"
      :icon="rightIcon"
      size="sm"
      class="ChipFilter-rightIcon"
    />
  </button>
</template>

<script setup lang="ts">
import Icon from '../Icon/Icon.vue';
import type { ChipFilterProps } from './ChipFilter.model';

defineProps<ChipFilterProps>();

defineEmits<{
  clear: []
}>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .ChipFilter {
    @include text.text(xs);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: vars.$dimensions-d-8;
    padding-block: vars.$dimensions-d-2;
    padding-inline: vars.$dimensions-d-3;
    border-style: solid;
    border-width: vars.$atoms-chips-chip-filter-border-stroke;
    border-radius: var(--chip-filter-border-radius, #{vars.$border-radius-full});
    background: none;
    appearance: none;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;

    // Default state
    --chip-filter-bg-color: #{vars.$atoms-chips-chip-filter-default-bg-color};
    --chip-filter-border-color: #{vars.$atoms-chips-chip-filter-default-border-color};
    --chip-filter-text-color: #{vars.$atoms-chips-chip-filter-default-label-color};
    --chip-filter-icon-color: #{vars.$atoms-chips-chip-filter-default-icon-color};

    background-color: var(--chip-filter-bg-color);
    border-color: var(--chip-filter-border-color);
    color: var(--chip-filter-text-color);

    &:hover:not(:disabled):not(.is-selected) {
      --chip-filter-bg-color: #{vars.$atoms-chips-chip-filter-hover-bg-color};
      --chip-filter-border-color: #{vars.$atoms-chips-chip-filter-hover-border-color};
      --chip-filter-text-color: #{vars.$atoms-chips-chip-filter-hover-label-color};
      --chip-filter-icon-color: #{vars.$atoms-chips-chip-filter-hover-icon-color};
    }

    &.is-selected {
      --chip-filter-bg-color: #{vars.$atoms-chips-chip-filter-selected-bg-color};
      --chip-filter-border-color: #{vars.$atoms-chips-chip-filter-selected-border-color};
      --chip-filter-text-color: #{vars.$atoms-chips-chip-filter-selected-label-color};
      --chip-filter-icon-color: #{vars.$atoms-chips-chip-filter-selected-icon-color};
    }

    &.is-disabled,
    &:disabled {
      --chip-filter-bg-color: #{vars.$atoms-chips-chip-filter-disabled-bg-color};
      --chip-filter-border-color: #{vars.$atoms-chips-chip-filter-disabled-border-color};
      --chip-filter-text-color: #{vars.$atoms-chips-chip-filter-disabled-label-color};
      --chip-filter-icon-color: #{vars.$atoms-chips-chip-filter-disabled-icon-color};
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid vars.$color-brand-secondary-900;
      outline-offset: 2px;
    }

    &-label {
      display: flex;
      align-items: center;
    }

    &-leftIcon,
    &-rightIcon,
    &-closeIcon {
      display: inline-flex;
      align-items: center;
      color: var(--chip-filter-icon-color);
    }

    &-closeIcon:focus-visible {
      outline: 2px solid vars.$color-brand-secondary-500;
      outline-offset: 2px;
      border-radius: 6px;
    }

  }
}
</style>
